import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ShieldCheck } from "lucide-react";
import paynovusLogoStacked from '@/assets/paynovus-logo-stacked-tagline.png';

const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .max(72, "Password is too long");

export default function CustomerResetPassword() {
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let mounted = true;

    // onAuthStateChange fires PASSWORD_RECOVERY (or SIGNED_IN after SDK exchange)
    // — must be set up BEFORE getSession so we don't miss the event.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setHasSession(!!session);
        if (session?.access_token) setAccessToken(session.access_token);
        setIsReady(true);
      }
    });

    // Also check for token in the URL hash (Supabase sends #access_token=... on some flows)
    const hashParams = new URLSearchParams(window.location.hash.replace('#', '?'));
    const hashToken = hashParams.get('access_token');
    const hashType  = hashParams.get('type');

    // Check for token_hash in query string (newer Supabase flow)
    const queryParams = new URLSearchParams(window.location.search);
    const tokenHash = queryParams.get('token_hash');
    const queryType = queryParams.get('type');

    const handleUrlToken = async () => {
      if (tokenHash && queryType === 'recovery') {
        // Exchange token_hash for a session
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: 'recovery',
        });
        if (!mounted) return;
        if (!error && data.session) {
          setHasSession(true);
          setAccessToken(data.session.access_token);
          setIsReady(true);
          return;
        }
      }

      if (hashToken && (hashType === 'recovery' || hashType === 'signup')) {
        // Older hash-fragment flow — set session directly
        const { data, error } = await supabase.auth.setSession({
          access_token: hashToken,
          refresh_token: hashParams.get('refresh_token') || '',
        });
        if (!mounted) return;
        if (!error && data.session) {
          setHasSession(true);
          setAccessToken(data.session.access_token);
          setIsReady(true);
          return;
        }
      }

      // Fallback: check existing session (e.g. page refresh or SDK auto-exchange)
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      if (data.session) {
        setHasSession(true);
        setAccessToken(data.session.access_token);
      }
      setIsReady(true);
    };

    void handleUrlToken();

    // Safety fallback: mark ready after 6s even if no event fires
    const fallbackTimer = setTimeout(() => {
      if (mounted) setIsReady(true);
    }, 6000);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearTimeout(fallbackTimer);
    };
  }, []);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid password");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsSaving(true);
    try {
      // Use the token captured from onAuthStateChange (getSession may return null for recovery sessions)
      const token = accessToken;
      if (!token) {
        setError("Session expired. Please request a new password reset link.");
        return;
      }

      // Use service-role edge function to bypass AAL2 requirement for MFA users
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-password-reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ mode: "update_password", password: parsed.data }),
      });
      const result = await res.json();
      if (!res.ok || result.error) {
        setError(result.error || "Could not update password");
        return;
      }
      setSuccess(true);
      await supabase.auth.signOut();
      navigate("/app/login", { replace: true });
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen hero-dark flex flex-col items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <img src={paynovusLogoStacked} alt="PayNovus" className="h-20 sm:h-24 w-auto mx-auto mb-6" />
          <div className="flex items-center justify-center gap-2 mb-2">
            <ShieldCheck className="h-6 w-6 text-accent" />
            <h1 className="text-2xl font-bold text-foreground">Reset Your Password</h1>
          </div>
          <p className="text-muted-foreground text-sm">
            {!isReady ? "Loading…" : hasSession ? "Choose a new password for your account." : "This link has expired. Please request a new one."}
          </p>
        </div>

        {success ? (
          <Alert className="border-accent/30 bg-accent/10">
            <AlertDescription className="text-accent text-center">
              Password updated! Redirecting you to login…
            </AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-foreground">New Password</Label>
            <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isSaving || !hasSession}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-foreground">Confirm Password</Label>
            <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isSaving || !hasSession}
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-full text-base font-semibold"
              disabled={isSaving || !hasSession || !password || !confirmPassword}
            >
              {isSaving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Updating…</>
              ) : (
                "Set New Password"
              )}
            </Button>

            {!hasSession && isReady && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate("/app/login")}
              >
                Back to Login
              </Button>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
